import React, { useEffect, useState } from "react";
import ReportTable from "../common/ReportTable";
import TableSkeleton from "../../utils/TableSkeleton";
import EmptyTableState from "../../utils/EmptyTableState";
import { customStyles } from "../../utils/DataTableCustomStyle";
import { logoutFun } from "../HomeSideNavBar";
import { getClientIdOfUser } from "../../utils/utility";
import {
  buildPaginationParams,
  DEFAULT_PAGE_SIZE,
  extractPaginationHeaders,
  PAGE_SIZE_OPTIONS,
} from "../../utils/paginationUtils";
import {
  fetchClientAuditLogs,
  fetchAllAuditLogs,
  AUDIT_ACTIONS,
  AUDIT_ENTITY_TYPES,
} from "./auditLogService";

const STATUS_STYLES = {
  SUCCESS: { background: "#dcfce7", color: "#166534" },
  FAILURE: { background: "#fee2e2", color: "#991b1b" },
};

const ACTION_STYLES = {
  LOGIN:                    { background: "#dbeafe", color: "#1e40af" },
  LOGIN_FAILURE:            { background: "#fee2e2", color: "#991b1b" },
  CREATE:                   { background: "#dcfce7", color: "#166534" },
  UPDATE:                   { background: "#fef9c3", color: "#854d0e" },
  DELETE:                   { background: "#fee2e2", color: "#991b1b" },
  STATUS_CHANGE:            { background: "#ede9fe", color: "#5b21b6" },
  ASSIGN:                   { background: "#dbeafe", color: "#1e40af" },
  UNASSIGN:                 { background: "#ffedd5", color: "#9a3412" },
  ACTIVATE:                 { background: "#dcfce7", color: "#166534" },
  DEACTIVATE:               { background: "#fee2e2", color: "#991b1b" },
  GENERATE:                 { background: "#ede9fe", color: "#5b21b6" },
  EXPORT:                   { background: "#fef9c3", color: "#854d0e" },
  PASSWORD_CHANGE:          { background: "#ffedd5", color: "#9a3412" },
  DEVICE_COMMAND:           { background: "#e0f2fe", color: "#0369a1" },
  DEVICE_COMMAND_QUEUED:    { background: "#fef3c7", color: "#92400e" },
  DEVICE_COMMAND_CANCELLED: { background: "#fce7f3", color: "#9d174d" },
  DEVICE_COMMAND_DELIVERED: { background: "#d1fae5", color: "#065f46" },
};

function Badge({ label, styles }) {
  const s = styles || { background: "#f3f4f6", color: "#374151" };
  return (
    <span
      style={{
        padding: "3px 8px",
        borderRadius: "10px",
        fontSize: "11px",
        fontWeight: "bold",
        ...s,
      }}
    >
      {label}
    </span>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Today's date as YYYY-MM-DD for the max attribute on date inputs. */
function todayStr() {
  return new Date().toISOString().split("T")[0];
}

/**
 * Converts a YYYY-MM-DD date string to an ISO datetime string.
 * from=true  → "2026-04-05T00:00:00"  (start of day)
 * from=false → "2026-04-05T23:59:59"  (end of day)
 */
function dateToDateTime(dateStr, isFrom) {
  if (!dateStr) return "";
  return `${dateStr}T${isFrom ? "00:00:00" : "23:59:59"}`;
}

export default function ListAuditLogs({ isSuperAdmin }) {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState({
    actorUsername: "",
    action: "",
    entityType: "",
    status: "",
    from: "",
    to: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});

  const clientId = isSuperAdmin ? null : getClientIdOfUser();

  const columns = [
    {
      name: "Time", id: "Time",
      selector: (row) => row.createdAt,
      sortable: true,
      width: "170px",
      cell: (row) => (
        <span style={{ fontSize: "12px", color: "#6b7280" }}>
          {formatDateTime(row.createdAt)}
        </span>
      ),
    },
    {
      name: "Actor", id: "Actor",
      selector: (row) => row.actorUsername,
      sortable: true,
      grow: 1,
      cell: (row) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: "13px" }}>
            {row.actorUsername}
          </div>
          {row.actorRole && (
            <div style={{ fontSize: "11px", color: "#9ca3af" }}>
              {row.actorRole}
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Action", id: "Action",
      selector: (row) => row.action,
      sortable: true,
      width: "175px",
      cell: (row) => (
        <Badge label={row.action} styles={ACTION_STYLES[row.action]} />
      ),
    },
    {
      name: "Entity", id: "Entity",
      selector: (row) => row.entityType,
      sortable: true,
      width: "130px",
      cell: (row) =>
        row.entityType ? (
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600 }}>
              {row.entityType}
            </div>
            {row.entityId && (
              <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                #{row.entityId}
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: "#d1d5db" }}>—</span>
        ),
    },
    {
      name: "Description", id: "Description",
      selector: (row) => row.description,
      grow: 2,
      cell: (row) => (
        <span style={{ fontSize: "12px" }}>{row.description || "—"}</span>
      ),
    },
    {
      name: "Status", id: "Status",
      selector: (row) => row.status,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <Badge label={row.status} styles={STATUS_STYLES[row.status]} />
      ),
    },
    {
      name: "IP Address", id: "IP Address",
      selector: (row) => row.ipAddress,
      width: "135px",
      cell: (row) => (
        <span style={{ fontSize: "11px", color: "#6b7280", fontFamily: "monospace" }}>
          {row.ipAddress}
        </span>
      ),
    },
  ];

  if (isSuperAdmin) {
    columns.splice(1, 0, {
      name: "Client", id: "Client",
      selector: (row) => row.clientId,
      sortable: true,
      width: "85px",
      cell: (row) => (
        <span style={{ fontSize: "12px" }}>{row.clientId || "—"}</span>
      ),
    });
  }

  const fetchData = async (page, size, activeFilters = appliedFilters) => {
    try {
      setIsLoading(true);
      setError(null);

      const pagination = buildPaginationParams(page, size);

      // Expand date-only filters to full day ranges before sending
      const params = { ...activeFilters };
      if (params.from) params.from = dateToDateTime(params.from, true);
      if (params.to)   params.to   = dateToDateTime(params.to, false);

      const response = isSuperAdmin
        ? await fetchAllAuditLogs(params, pagination)
        : await fetchClientAuditLogs(clientId, params, pagination);

      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : [];
        setLogs(data);
        const paginationInfo = extractPaginationHeaders(response);
        setTotalRows(paginationInfo ? paginationInfo.totalCount : data.length);
      } else if (response.status === 401 || response.status === 403) {
        logoutFun();
      } else {
        setError("Failed to load audit logs");
      }
    } catch (err) {
      console.error("Error fetching audit logs:", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        logoutFun();
      } else {
        setError("Error loading audit logs");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, perPage, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, perPage);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchData(page, newPerPage);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    const active = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v) active[k] = v;
    });
    setAppliedFilters(active);
    setCurrentPage(1);
    fetchData(1, perPage, active);
  };

  const clearFilters = () => {
    const empty = { actorUsername: "", action: "", entityType: "", status: "", from: "", to: "" };
    setFilters(empty);
    setAppliedFilters({});
    setCurrentPage(1);
    fetchData(1, perPage, {});
  };

  const today = todayStr();

  const selectStyle = {
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    fontSize: "13px",
    background: "#fff",
    minWidth: "130px",
  };

  const dateStyle = {
    ...selectStyle,
    minWidth: "140px",
  };

  const inputClass = "h-9 px-3 text-base rounded-md bg-white border border-ink-200 shadow-inset text-ink-900 placeholder:text-ink-400 hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring";

  return (
    <div className="space-y-3">
      <div className="bg-white border border-ink-200 rounded-xl shadow-xs p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-ink-500 mb-1">Search by user</label>
          <input
            type="text"
            name="actorUsername"
            value={filters.actorUsername}
            onChange={handleFilterChange}
            placeholder="username, email…"
            className={`${inputClass} w-full`}
          />
        </div>

        <div className="min-w-[160px]">
          <label className="block text-xs font-medium text-ink-500 mb-1">Action</label>
          <select name="action" value={filters.action} onChange={handleFilterChange} className={`${inputClass} pr-8`}>
            <option value="">All actions</option>
            {AUDIT_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div className="min-w-[160px]">
          <label className="block text-xs font-medium text-ink-500 mb-1">Entity</label>
          <select name="entityType" value={filters.entityType} onChange={handleFilterChange} className={`${inputClass} pr-8`}>
            <option value="">All entities</option>
            {AUDIT_ENTITY_TYPES.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>

        <div className="min-w-[130px]">
          <label className="block text-xs font-medium text-ink-500 mb-1">Status</label>
          <select name="status" value={filters.status} onChange={handleFilterChange} className={`${inputClass} pr-8`}>
            <option value="">All statuses</option>
            <option value="SUCCESS">SUCCESS</option>
            <option value="FAILURE">FAILURE</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-ink-500 mb-1">From</label>
          <input type="date" name="from" value={filters.from} max={today} onChange={handleFilterChange} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-500 mb-1">To</label>
          <input type="date" name="to" value={filters.to} max={today} onChange={handleFilterChange} className={inputClass} />
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={clearFilters}
            className="inline-flex items-center h-9 px-4 text-sm font-medium rounded-md bg-white border border-ink-200 text-ink-700 hover:bg-ink-50 transition-colors"
          >
            Clear
          </button>
          <button
            onClick={applyFilters}
            className="inline-flex items-center h-9 px-4 text-sm font-medium rounded-md bg-accent-500 text-white hover:bg-accent-600 transition-colors"
          >
            Apply
          </button>
        </div>
      </div>

      <div className="users-table-wrapper rounded-xl border border-ink-200 bg-white shadow-xs overflow-hidden">
        <ReportTable
          columns={columns}
          data={logs}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          responsive
          highlightOnHover
          striped
          progressPending={isLoading}
          progressComponent={<TableSkeleton />}
          noDataComponent={
            error ? (
              <EmptyTableState variant="error" message={error} />
            ) : (
              <EmptyTableState message="No audit log entries found" />
            )
          }
          keyField="auditId"
          customStyles={customStyles}
          defaultSortFieldId={1}
          defaultSortAsc={false}
        />
      </div>
    </div>
  );
}
